import { request } from '../../../services/request';
import { CardFeeDetailsDTO, CardFeeDetailsV2DTO } from '../types';
import { ICacheStore } from '../context/cache-context';
import { Api } from 'src/common';

export type TerminalFee = {
  fixedFee: number;
  deviceVendorId: string;
  deviceVendorName: string;
  deviceModelId: string;
  deviceModelName: string;
};

export type TerminalFeeResponse = {
  result: {
    TerminalFeesData: TerminalFee[];
    userId: string;
    acquiringProduct: string;
  };
  success: boolean;
  status: number;
};

export interface IFeesService {
  getCardFees(
    monthlyTurnover: number,
    abortController?: AbortController,
    enableCache?: boolean,
  ): Promise<CardFeeDetailsDTO[]>;
  getCardFeesV2(
    products: string[],
    turnover?: number,
    filterByFeeCategory?: 'Y' | 'N',
    abortController?: AbortController,
  ): Promise<CardFeeDetailsV2DTO | undefined>;
  getTerminalFees(abortController?: AbortController): Promise<TerminalFee[]>;
}

type CardFeesCache = {
  cardFees: CardFeeDetailsDTO[];
  turnover: number;
};

export class FeesService implements IFeesService {
  private baseUrl: string;
  private cache?: ICacheStore;

  // constants - URLS
  private CARD_FEES_ENDPOINT_PATH = 'cardfees';
  private ONBOARDING_FEES_ENDPOINT_PATH = 'onboardingfees';
  private CARD_FEES_V2_ENDPOINT_PATH = 'cardfeesv2';
  private TERMINAL_FEES_ENDPOINT_PATH = 'terminalfees';

  constructor(baseUrl: string, cache?: ICacheStore) {
    this.baseUrl = baseUrl;
    this.cache = cache;
  }

  getCardFees(
    monthlyTurnover: number,
    abortController?: AbortController,
    enableCache: boolean = true,
  ): Promise<CardFeeDetailsDTO[]> {
    if (this.cache) {
      const cardFeesCache: CardFeesCache = this.cache.get(this.CARD_FEES_ENDPOINT_PATH);
      if (cardFeesCache?.cardFees && cardFeesCache?.turnover === monthlyTurnover) {
        return Promise.resolve(cardFeesCache.cardFees);
      }
    }

    return this.getOnboardingFees(abortController)
      .then((onboardingFeesResponse) => {
        return request(
          `${this.CARD_FEES_ENDPOINT_PATH}?amount=${monthlyTurnover}&acquiringProduct=${onboardingFeesResponse.acquiringProduct}&userId=${onboardingFeesResponse.userId}`,
          {
            fetchOptions: {
              method: 'GET',
              signal: abortController?.signal || null,
            },
          },
          this.baseUrl,
          null,
          false,
          true,
        ).then((cardFeesResponse) => {
          const responseCode = cardFeesResponse?.status;
          const responseBody = cardFeesResponse?.result;

          if (responseCode !== 200 && !abortController?.signal?.aborted) {
            throw new Error(
              `Card fees revamp: error response from salesforce ${this.CARD_FEES_ENDPOINT_PATH} GET endpoint: response code: ${responseCode}`,
            );
          }

          const cardFees = (responseBody?.cardFeesData || []) as CardFeeDetailsDTO[];

          if (this.cache && enableCache) {
            this.cache.set(this.CARD_FEES_ENDPOINT_PATH, {
              cardFees: cardFees,
              turnover: monthlyTurnover,
            } as CardFeesCache);
          }

          return cardFees;
        });
      })
      .catch(() => {
        //  onboardingFeesResponse is undefined
        return [];
      });
  }

  private getOnboardingFees(abortController?: AbortController): Promise<OnboardingFeesSFResponse> {
    return request(
      this.ONBOARDING_FEES_ENDPOINT_PATH,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController?.signal || null,
        },
      },
      this.baseUrl,
      null,
      false,
      true,
    ).then((response) => {
      const responseCode = response?.status;
      const responseBody = response?.result;

      if (responseCode !== 200 && !abortController?.signal?.aborted) {
        throw new Error(
          `Onboarding fees revamp: error response from salesforce ${this.ONBOARDING_FEES_ENDPOINT_PATH} GET endpoint: response code: ${responseCode}`,
        );
      }

      return responseBody as OnboardingFeesSFResponse;
    });
  }

  async getCardFeesV2(
    products?: string[],
    turnover?: number,
    filterByFeeCategory?: 'Y' | 'N',
    abortController?: AbortController,
  ): Promise<CardFeeDetailsV2DTO | undefined> {
    const params = {
      products: products,
      turnover: turnover,
      filterByFeeCategory: filterByFeeCategory,
    };
    const queryParams = Object.entries(params)
      .filter(([, value]) => {
        if (Array.isArray(value)) {
          return value !== undefined && value !== null && value.length !== 0;
        }
        return value !== undefined && value !== null;
      })
      .map(([key, value]) => {
        if (!value) return;
        if (Array.isArray(value)) {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`;
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      });
    const queryString = queryParams.join('&');
    const query = `${this.CARD_FEES_V2_ENDPOINT_PATH}?${queryString}`;

    try {
      const { response, status } = await Api.get<CardFeeDetailsV2DTO>(query, {
        signal: abortController?.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (status !== 200) {
        throw new Error(
          `Onboarding fees slider: error response ${query} GET endpoint: response code: ${status}, response: ${JSON.stringify(
            response,
          )}`,
        );
      }
      return response;
    } catch (error) {
      console.error(error);
      throw new Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }
  }

  async getTerminalFees(abortController?: AbortController): Promise<TerminalFee[]> {

    const cachedTerminalFees = this.cache?.get(this.TERMINAL_FEES_ENDPOINT_PATH);

    if (cachedTerminalFees) {
      return cachedTerminalFees;
    }

    const url = `${this.TERMINAL_FEES_ENDPOINT_PATH}?allDevices=true`

    try {
      const { response, status } = await Api.get<TerminalFeeResponse>(url, {
        signal: abortController?.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (status !== 200) {
        return [];
      }

      this.cache?.set(this.TERMINAL_FEES_ENDPOINT_PATH, response.result.TerminalFeesData);

      return response.result.TerminalFeesData;
    } catch (error) {
      return [];
    }
  }
}


export type TerminalFeeSFResponse = {
  deviceModelName: string;
  fixedFee: number;
};

export type OnboardingFeesSFResponse = {
  acquiringProduct: string;
  userId: string;
};
